import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => localStorage.getItem('token');

const tradesApiClient = axios.create({
  baseURL: config.api.tradesServiceUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

tradesApiClient.interceptors.request.use(
  (cfg) => {
    const token = getToken();
    if (token) {
      cfg.headers.Authorization = `Bearer ${token}`;
    }
    return cfg;
  },
  (error) => Promise.reject(error),
);

applyAuthRetryInterceptor(tradesApiClient);
applyTokenRefreshInterceptor(tradesApiClient);

const normalizeCompanyImportJobResponse = (payload) => {
  const data = payload || {};
  const job = data.job || (data.job_id ? data : null);

  return {
    ...data,
    success: data.success ?? Boolean(job?.job_id),
    job,
  };
};

const tradesService = {
  getTrades: async (limit = 200, targetUserId = null) => {
    const params = { limit };
    if (targetUserId) {
      params.target_user_id = targetUserId;
    }
    const response = await tradesApiClient.get('/trades', {
      params,
    });
    return response.data;
  },

  getTradeOptions: async (limit = 1000, targetUserId = null) => {
    const params = { limit };
    if (targetUserId) {
      params.target_user_id = targetUserId;
    }
    const response = await tradesApiClient.get('/trades/options', {
      params,
    });
    return response.data;
  },

  createTrade: async (tradePayload) => {
    const response = await tradesApiClient.post('/trades', tradePayload);
    return response.data;
  },

  getSavedAccounts: async () => {
    const response = await tradesApiClient.get('/trades/accounts');
    return response.data;
  },

  createSavedAccount: async (accountPayload) => {
    const response = await tradesApiClient.post('/trades/accounts', accountPayload);
    return response.data;
  },

  updateSavedAccount: async (accountId, accountPayload) => {
    const response = await tradesApiClient.put(
      `/trades/accounts/${encodeURIComponent(accountId)}`,
      accountPayload,
    );
    return response.data;
  },

  deleteSavedAccount: async (accountId) => {
    const response = await tradesApiClient.delete(
      `/trades/accounts/${encodeURIComponent(accountId)}`,
    );
    return response.data;
  },

  updateTrade: async (tradeId, tradePayload) => {
    const response = await tradesApiClient.put(`/trades/${encodeURIComponent(tradeId)}`, tradePayload);
    return response.data;
  },

  deleteTrade: async (tradeId) => {
    const response = await tradesApiClient.delete(`/trades/${encodeURIComponent(tradeId)}`);
    return response.data;
  },

  startStatementImportJob: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await tradesApiClient.post('/trades/statement-jobs', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000,
    });
    return response.data.job;
  },

  getStatementImportJob: async (jobId) => {
    const response = await tradesApiClient.get(
      `/trades/statement-jobs/${encodeURIComponent(jobId)}`,
    );
    return response.data.job;
  },

  // Bedrock extraction can outlast API Gateway's 30s limit, so the
  // statement is extracted in a background job that we poll here.
  extractStatement: async (file, { pollIntervalMs = 2000, maxWaitMs = 17 * 60 * 1000 } = {}) => {
    let job = await tradesService.startStatementImportJob(file);
    const deadline = Date.now() + maxWaitMs;
    while (job.status === 'processing') {
      if (Date.now() > deadline) {
        throw new Error('Statement extraction is taking too long. Please try again.');
      }
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
      job = await tradesService.getStatementImportJob(job.job_id);
    }
    if (job.status === 'failed') {
      throw new Error(job.error || 'Statement extraction failed.');
    }
    return job.job_id;
  },

  importStatement: async ({
    file,
    jobId,
    brokerage,
    account_name,
    account_number,
    account_index,
    dryRun = true,
  }) => {
    const formData = new FormData();
    if (jobId) {
      formData.append('job_id', jobId);
    } else if (file) {
      formData.append('file', file);
    }
    if (brokerage) {
      formData.append('brokerage', brokerage);
    }
    if (account_name) {
      formData.append('account_name', account_name);
    }
    if (account_number) {
      formData.append('account_number', account_number);
    }
    if (account_index !== undefined && account_index !== null) {
      formData.append('account_index', String(account_index));
    }
    formData.append('dry_run', String(dryRun));

    const response = await tradesApiClient.post('/trades/import-statement', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000,
    });
    return response.data;
  },

  previewCompanyReport: async ({ file, quarter }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (quarter) {
      formData.append('quarter', quarter);
    }

    const response = await tradesApiClient.post('/trades/company/reports/preview', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 180000,
    });
    return response.data;
  },

  startCompanyImportJob: async ({ file, quarter }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (quarter) {
      formData.append('quarter', quarter);
    }

    const response = await tradesApiClient.post('/trades/company/jobs', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 180000,
    });
    return normalizeCompanyImportJobResponse(response.data);
  },

  getCompanyImportJob: async (jobId) => {
    const response = await tradesApiClient.get(
      `/trades/company/jobs/${encodeURIComponent(jobId)}`,
    );
    return normalizeCompanyImportJobResponse(response.data);
  },

  getCompanyImportJobs: async (limit = 25) => {
    const response = await tradesApiClient.get('/trades/company/jobs', {
      params: { limit },
    });
    return response.data;
  },

  getCompanyReports: async (limit = 100) => {
    const response = await tradesApiClient.get('/trades/company/reports', {
      params: { limit },
    });
    return response.data;
  },

  deleteCompanyReport: async (reportId) => {
    const response = await tradesApiClient.delete(
      `/trades/company/reports/${encodeURIComponent(reportId)}`,
    );
    return response.data;
  },

  downloadCompanyReport: async (reportId) => {
    const response = await tradesApiClient.get(
      `/trades/company/reports/${encodeURIComponent(reportId)}/download`,
      {
        responseType: 'blob',
        timeout: 120000,
      },
    );

    const disposition = String(response.headers?.['content-disposition'] || '');
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    return {
      blob: response.data,
      filename: filenameMatch?.[1] || 'company-report.xlsx',
    };
  },

  getCompanyQuarterSummary: async (quarter) => {
    const response = await tradesApiClient.get('/trades/company/summary', {
      params: { quarter },
      timeout: 120000,
    });
    return response.data;
  },

  getCompanyTrades: async ({ quarter, broker, symbol, action, limit = 500 }) => {
    const params = { quarter, limit };
    if (broker) params.broker = broker;
    if (symbol) params.symbol = symbol;
    if (action) params.action = action;

    const response = await tradesApiClient.get('/trades/company/trades', {
      params,
      timeout: 120000,
    });
    return response.data;
  },

  startComplianceScan: async ({ startDate, endDate, label, nearMissWindowDays }) => {
    const payload = { start_date: startDate, end_date: endDate };
    if (label) payload.label = label;
    if (nearMissWindowDays !== undefined && nearMissWindowDays !== null) {
      payload.near_miss_window_days = Number(nearMissWindowDays);
    }

    const response = await tradesApiClient.post('/trades/compliance/scans', payload);
    return response.data;
  },

  getComplianceScans: async (limit = 25) => {
    const response = await tradesApiClient.get('/trades/compliance/scans', {
      params: { limit },
    });
    return response.data;
  },

  getComplianceScan: async (scanId, statusFilter = null) => {
    const params = {};
    if (statusFilter) params.status = statusFilter;

    const response = await tradesApiClient.get(
      `/trades/compliance/scans/${encodeURIComponent(scanId)}`,
      { params, timeout: 120000 },
    );
    return response.data;
  },

  deleteComplianceScan: async (scanId) => {
    const response = await tradesApiClient.delete(
      `/trades/compliance/scans/${encodeURIComponent(scanId)}`,
    );
    return response.data;
  },

  updateComplianceFinding: async (scanId, findingId, { disposition, note }) => {
    const response = await tradesApiClient.patch(
      `/trades/compliance/scans/${encodeURIComponent(scanId)}/findings/${encodeURIComponent(findingId)}`,
      { disposition, disposition_note: note || '' },
    );
    return response.data;
  },

  generateComplianceReport: async (scanId) => {
    const response = await tradesApiClient.post(
      `/trades/compliance/scans/${encodeURIComponent(scanId)}/report`,
      {},
      { timeout: 120000 },
    );
    return response.data;
  },

  getComplianceReport: async (scanId) => {
    const response = await tradesApiClient.get(
      `/trades/compliance/scans/${encodeURIComponent(scanId)}/report`,
    );
    return response.data;
  },
};

export default tradesService;
